import { memo, useMemo } from 'react'
import { rem } from 'polished'
import styled from 'styled-components'
import { FormattedMessage } from 'react-intl'

import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import useBedbankPackageViews from 'hooks/useBedbankPackageViews'
import { groupBy, min } from 'lib/array/arrayUtils'
import BedbankSummaryPromotion from './BedbankSummaryPromotion'
import type { OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'

interface Props {
  offer: App.BedbankOffer
  rooms: Array<OfferPageRoom>
  duration: number
  checkInDate?: string
  checkOutDate?: string
  flightPrice?: number
  flightsEnabled: boolean
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: ${rem(36)} !important;

  > * + * {
    margin-top: ${rem(20)};
  }
`

/** @deprecated */
function BedbankSummaryPromotions({ offer, rooms, duration, checkInDate, checkOutDate, flightsEnabled, flightPrice }: Props) {
  const [packageViews, fetchingRates] = useBedbankPackageViews(offer, true)

  const availablePackages = useMemo(() => {
    return packageViews.filter((pkg) => {
      return pkg.available && pkg.cheapestRate?.promotionId
    })
  }, [packageViews])

  const availablePromotions = useMemo(() => {
    const promotions = groupBy(availablePackages, (pkg) => pkg.cheapestRate?.promotionId)

    return Array.from(promotions).map(([promotionId, packages]) => {
      return {
        id: promotionId,
        packageView: min(packages, (pkg) => pkg.cheapestRate?.totals.inclusive ?? pkg.package.sell?.price ?? 0) ?? packages[0],
      }
    })
  }, [availablePackages])

  const hasPromotions = availablePromotions.length > 0 && !fetchingRates

  if (!hasPromotions) return null

  return <Container>
    <div>
      <Heading variant="heading4">
        <FormattedMessage
          id="6MvT8wysuzIlQe6UpUnR"
          defaultMessage="Current promotions"
          description="Section heading for current promotions on bedbank offer page"
        />
      </Heading>
      <BodyText variant="medium" as="p">
        <FormattedMessage
          id="rE-ahfAHbe_lWYYTRoBX"
          defaultMessage="Available for select rooms only"
          description="Subtitle for current promotions section on bedbank offer page"
        />
      </BodyText>
    </div>

    {availablePromotions.map((promotion) => (
      <BedbankSummaryPromotion
        key={promotion.id}
        offer={offer}
        packageView={promotion.packageView}
        rooms={rooms}
        duration={duration}
        checkInDate={checkInDate}
        checkOutDate={checkOutDate}
        flightsEnabled={flightsEnabled}
        flightPrice={flightPrice}
      />
    ))}
  </Container>
}

export default memo(BedbankSummaryPromotions)
